import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmd } from '../locale.js';
import type { Database } from '@cafe-pos/supabase';
import { departmentColor } from '@cafe-pos/shared';
import { supabase } from '../supabase.js';
import { parseCsv, readFileAsText } from '../lib/csv.js';
import { ChefHat, Pencil, Printer, Trash2 } from 'lucide-react';
import { Button, Callout, Modal, PageHeader } from '../ui/index.js';

type CategoryRow = Database['public']['Tables']['categories']['Row'];
type ProductRow = Database['public']['Tables']['products']['Row'];
type RecipeRow = Database['public']['Tables']['recipes']['Row'];


interface NewCategory {
  name: string;
  sortOrder: string;
  printKitchen: boolean;
  departmentId: string;
}
const BLANK_CAT: NewCategory = {
  name: '',
  sortOrder: '0',
  printKitchen: false,
  departmentId: '1',
};

interface NewProduct {
  name: string;
  categoryId: string;
  price: string;
  unit: string;
  adgCode: string;
  productCode: string;
  recipeId: string;
}

const BLANK: NewProduct = {
  name: '',
  categoryId: '',
  price: '',
  unit: 'հատ',
  adgCode: '',
  productCode: '',
  recipeId: '',
};

// A single price-affecting alternative for the price-list preview. Collected by
// walking a product's recipe tree (recipe → sub-recipes) and gathering every
// choice-group option AND ingredient substitute, each with its AMD surcharge.
interface PriceVariation {
  label: string;
  priceDelta: number;
}

export function MenuManager({
  clientId,
  onNavigateToRecipe,
}: {
  clientId: string | null;
  onNavigateToRecipe?: (recipeId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [recipes, setRecipes] = useState<RecipeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // category form
  const [catForm, setCatForm] = useState<NewCategory>(BLANK_CAT);
  const [catSaving, setCatSaving] = useState(false);

  // category inline edit
  const [catEditId, setCatEditId] = useState<string | null>(null);
  const [catEdit, setCatEdit] = useState<NewCategory>(BLANK_CAT);
  const [catEditSaving, setCatEditSaving] = useState(false);
  const [categoryPending, setCategoryPending] = useState<CategoryRow | null>(null);
  const [categoryToggleBusy, setCategoryToggleBusy] = useState(false);

  // product form
  const [form, setForm] = useState<NewProduct>(BLANK);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);
  const csvRef = useRef<HTMLInputElement>(null);

  // product inline edit
  const [prodEditId, setProdEditId] = useState<string | null>(null);
  interface EditProduct {
    name: string;
    price: string;
    unit: string;
    adgCode: string;
    productCode: string;
    categoryId: string;
  }
  const [prodEdit, setProdEdit] = useState<EditProduct>({
    name: '',
    price: '',
    unit: '',
    adgCode: '',
    productCode: '',
    categoryId: '',
  });
  const [prodEditSaving, setProdEditSaving] = useState(false);

  // Recipe cost per product — fixed BOM plus min/max when recipe uses choice groups (e.g. wine pours).
  const [recipeCostByProduct, setRecipeCostByProduct] = useState<
    Map<string, { min: number; max: number }>
  >(new Map());

  // price-list preview: price variations per product + selected category
  const [variationsByProduct, setVariationsByProduct] = useState<Map<string, PriceVariation[]>>(
    new Map(),
  );
  const [priceListCat, setPriceListCat] = useState<string>(''); // '' = all categories

  const [search, setSearch] = useState('');

  async function load() {
    if (!supabase) return;
    setLoading(true);
    setError(null);
    const [cats, prods, cards] = await Promise.all([
      supabase.from('categories').select('*').order('sort_order'),
      supabase.from('products').select('*').order('name'),
      supabase.from('recipes').select('*').order('name'),
    ]);
    if (cats.error) setError(cats.error.message);
    else {
      setCategories(cats.data);
      // Pre-select the first category if the form still has no category chosen
      if (cats.data.length > 0) {
        setForm((prev) => ({ ...prev, categoryId: prev.categoryId || cats.data[0]!.id }));
      }
    }
    if (prods.error) setError(prods.error.message);
    else setProducts(prods.data);
    if (!cards.error) setRecipes(cards.data);
    if (!prods.error) {
      void loadPriceVariations(prods.data);
      if (!cards.error) void loadRecipeCosts(prods.data, cards.data);
    }
    setLoading(false);
  }

  // Compute ingredient cost per product: fixed recipe_items + min/max across choice options.
  async function loadRecipeCosts(prods: ProductRow[], recipeList: RecipeRow[]) {
    if (!supabase) return;

    const itemsRes = await supabase
      .from('recipe_items')
      .select('recipe_id, ingredient_id, sub_recipe_id, quantity');
    type CostItem = {
      recipe_id: string;
      ingredient_id: string | null;
      sub_recipe_id: string | null;
      quantity: number;
    };
    const allItems = (itemsRes.data ?? []) as CostItem[];
    const itemsByRecipe = new Map<string, CostItem[]>();
    for (const it of allItems) {
      if (!itemsByRecipe.has(it.recipe_id)) itemsByRecipe.set(it.recipe_id, []);
      itemsByRecipe.get(it.recipe_id)!.push(it);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cgRes = await (supabase as any)
      .from('recipe_choice_groups')
      .select('id, recipe_id');
    type ChoiceGroupRow = { id: string; recipe_id: string };
    const choiceGroups = (cgRes.data ?? []) as ChoiceGroupRow[];
    const groupsByRecipe = new Map<string, string[]>();
    for (const g of choiceGroups) {
      if (!groupsByRecipe.has(g.recipe_id)) groupsByRecipe.set(g.recipe_id, []);
      groupsByRecipe.get(g.recipe_id)!.push(g.id);
    }

    const groupIds = choiceGroups.map((g) => g.id);
    type ChoiceOptRow = { group_id: string; ingredient_id: string; quantity: number };
    const optRes =
      groupIds.length > 0
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await (supabase as any)
            .from('recipe_choice_options')
            .select('group_id, ingredient_id, quantity')
            .in('group_id', groupIds)
        : { data: [] as ChoiceOptRow[] };
    const choiceOpts = (optRes.data ?? []) as ChoiceOptRow[];
    const optsByGroup = new Map<string, ChoiceOptRow[]>();
    for (const o of choiceOpts) {
      if (!optsByGroup.has(o.group_id)) optsByGroup.set(o.group_id, []);
      optsByGroup.get(o.group_id)!.push(o);
    }

    const ingIds = new Set<string>();
    for (const it of allItems) {
      if (it.ingredient_id) ingIds.add(it.ingredient_id);
    }
    for (const o of choiceOpts) ingIds.add(o.ingredient_id);
    const ingCost = new Map<string, number>();
    if (ingIds.size > 0) {
      const ingRes = await supabase
        .from('ingredients')
        .select('id, cost_price')
        .in('id', [...ingIds]);
      for (const i of ingRes.data ?? []) {
        ingCost.set(i.id, Number(i.cost_price ?? 0));
      }
    }

    type RecipeMeta = { is_prep: boolean; prep_yield: number | null };
    const recipeMeta = new Map<string, RecipeMeta>(
      recipeList.map((r) => [
        r.id,
        {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          is_prep: (r as any).is_prep ?? false,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          prep_yield: (r as any).prep_yield ?? null,
        },
      ]),
    );

    function calcCostRange(recipeId: string, multiplier: number, depth = 0): { min: number; max: number } {
      if (depth > 10) return { min: 0, max: 0 };
      const meta = recipeMeta.get(recipeId);
      const effective =
        meta?.is_prep && meta.prep_yield ? multiplier / meta.prep_yield : multiplier;

      let fixedMin = 0;
      let fixedMax = 0;
      for (const it of itemsByRecipe.get(recipeId) ?? []) {
        if (it.sub_recipe_id) {
          const sub = calcCostRange(it.sub_recipe_id, effective * Number(it.quantity), depth + 1);
          fixedMin += sub.min;
          fixedMax += sub.max;
        } else if (it.ingredient_id) {
          const c = (ingCost.get(it.ingredient_id) ?? 0) * effective * Number(it.quantity);
          fixedMin += c;
          fixedMax += c;
        }
      }

      const groupIdsForRecipe = groupsByRecipe.get(recipeId) ?? [];
      let choiceMin = 0;
      let choiceMax = 0;
      for (const gid of groupIdsForRecipe) {
        const optionCosts = (optsByGroup.get(gid) ?? []).map(
          (o) => (ingCost.get(o.ingredient_id) ?? 0) * effective * Number(o.quantity ?? 1),
        );
        if (optionCosts.length === 0) continue;
        choiceMin += Math.min(...optionCosts);
        choiceMax += Math.max(...optionCosts);
      }

      if (groupIdsForRecipe.length === 0) {
        return { min: fixedMin, max: fixedMax };
      }
      return { min: fixedMin + choiceMin, max: fixedMax + choiceMax };
    }

    const byProduct = new Map<string, { min: number; max: number }>();
    for (const p of prods) {
      if (!p.recipe_id) continue;
      byProduct.set(p.id, calcCostRange(p.recipe_id, 1));
    }
    setRecipeCostByProduct(byProduct);
  }

  // Load every price-affecting alternative for the price-list preview. For each
  // product we walk its recipe tree (recipe → sub-recipes, recursively) and
  // collect both choice-group options and ingredient substitutes, each with its
  // AMD surcharge. recipe_choice_* / recipe_item_substitutes tables are not in
  // the generated types yet, hence the casts.
  async function loadPriceVariations(prods: ProductRow[]) {
    if (!supabase) return;
    const rootRecipeIds = [
      ...new Set(prods.map((p) => p.recipe_id).filter((id): id is string => !!id)),
    ];
    if (rootRecipeIds.length === 0) {
      setVariationsByProduct(new Map());
      return;
    }

    // 1. All recipe items (needed to walk the sub-recipe tree + find substitutes).
    const itemsRes = await supabase
      .from('recipe_items')
      .select('id, recipe_id, ingredient_id, sub_recipe_id');
    type ItemRow = {
      id: string;
      recipe_id: string;
      ingredient_id: string | null;
      sub_recipe_id: string | null;
    };
    const items = (itemsRes.data ?? []) as ItemRow[];
    const itemsByRecipe = new Map<string, ItemRow[]>();
    for (const it of items) {
      if (!itemsByRecipe.has(it.recipe_id)) itemsByRecipe.set(it.recipe_id, []);
      itemsByRecipe.get(it.recipe_id)!.push(it);
    }

    // 2. Substitutes keyed by recipe_item_id.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const subsRes = await (supabase as any)
      .from('recipe_item_substitutes')
      .select('recipe_item_id, ingredient_id, price_delta');
    type SubRow = { recipe_item_id: string; ingredient_id: string; price_delta: number };
    const subs = (subsRes.data ?? []) as SubRow[];
    const subsByItem = new Map<string, SubRow[]>();
    for (const s of subs) {
      if (!subsByItem.has(s.recipe_item_id)) subsByItem.set(s.recipe_item_id, []);
      subsByItem.get(s.recipe_item_id)!.push(s);
    }

    // 3. Choice groups + options, grouped by recipe.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cgRes = await (supabase as any)
      .from('recipe_choice_groups')
      .select('id, recipe_id, sort_order')
      .order('sort_order');
    const groups = (cgRes.data ?? []) as { id: string; recipe_id: string }[];
    const groupRecipe = new Map(groups.map((g) => [g.id, g.recipe_id]));
    const groupIds = groups.map((g) => g.id);
    const optRes =
      groupIds.length > 0
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await (supabase as any)
            .from('recipe_choice_options')
            .select('group_id, ingredient_id, price_delta, sort_order')
            .in('group_id', groupIds)
            .order('sort_order')
        : { data: [] };
    type OptRow = { group_id: string; ingredient_id: string; price_delta: number };
    const opts = (optRes.data ?? []) as OptRow[];
    const optsByRecipe = new Map<string, OptRow[]>();
    for (const o of opts) {
      const rid = groupRecipe.get(o.group_id);
      if (!rid) continue;
      if (!optsByRecipe.has(rid)) optsByRecipe.set(rid, []);
      optsByRecipe.get(rid)!.push(o);
    }

    // 4. Resolve ingredient names for everything referenced.
    const ingIds = new Set<string>();
    for (const s of subs) ingIds.add(s.ingredient_id);
    for (const o of opts) ingIds.add(o.ingredient_id);
    const ingRes =
      ingIds.size > 0
        ? await supabase
            .from('ingredients')
            .select('id, name')
            .in('id', [...ingIds])
        : { data: [] as { id: string; name: string }[] };
    const ingName = new Map((ingRes.data ?? []).map((i) => [i.id, i.name]));

    // 5. For each product, walk its recipe tree and collect distinct variations.
    const byProduct = new Map<string, PriceVariation[]>();
    for (const p of prods) {
      if (!p.recipe_id) continue;
      const variations: PriceVariation[] = [];
      const seen = new Set<string>();
      const visited = new Set<string>();
      const stack: string[] = [p.recipe_id];
      const push = (label: string, priceDelta: number) => {
        const key = `${label}|${priceDelta}`;
        if (seen.has(key)) return;
        seen.add(key);
        variations.push({ label, priceDelta });
      };
      while (stack.length > 0) {
        const rid = stack.pop()!;
        if (visited.has(rid)) continue; // cycle / repeat guard
        visited.add(rid);
        for (const o of optsByRecipe.get(rid) ?? []) {
          push(ingName.get(o.ingredient_id) ?? o.ingredient_id, o.price_delta ?? 0);
        }
        for (const it of itemsByRecipe.get(rid) ?? []) {
          for (const s of subsByItem.get(it.id) ?? []) {
            push(ingName.get(s.ingredient_id) ?? s.ingredient_id, s.price_delta ?? 0);
          }
          if (it.sub_recipe_id) stack.push(it.sub_recipe_id);
        }
      }
      if (variations.length > 0) {
        variations.sort((a, b) => a.priceDelta - b.priceDelta);
        byProduct.set(p.id, variations);
      }
    }
    setVariationsByProduct(byProduct);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function addCategory() {
    if (!supabase) return;
    if (!clientId) {
      setError(t('menu.noClient'));
      return;
    }
    if (!catForm.name.trim()) {
      setError(t('menu.categoryNameRequired'));
      return;
    }
    const departmentId = parseDepartmentId(catForm.departmentId);
    if (departmentId == null) {
      setError(t('menu.hdmDeptInvalid'));
      return;
    }
    setCatSaving(true);
    setError(null);
    const { error: err } = await supabase.from('categories').insert({
      client_id: clientId,
      name: catForm.name.trim(),
      sort_order: Number(catForm.sortOrder) || 0,
      print_kitchen: catForm.printKitchen,
      department_id: departmentId,
    });
    setCatSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setCatForm(BLANK_CAT);
    void load();
  }

  async function confirmCategoryToggle() {
    if (!supabase || !categoryPending) return;
    const hiding = categoryPending.active !== false;
    setCategoryToggleBusy(true);
    setError(null);
    const { error: err } = await supabase
      .from('categories')
      .update({ active: !hiding })
      .eq('id', categoryPending.id);
    setCategoryToggleBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setCategoryPending(null);
    void load();
  }

  async function removeCategory(id: string) {
    if (!supabase) return;
    if (!confirm(t('menu.deleteCategoryConfirm'))) return;
    const { error: err } = await supabase.from('categories').delete().eq('id', id);
    if (err) setError(err.message);
    else void load();
  }

  function startCatEdit(c: CategoryRow) {
    setCatEditId(c.id);
    setCatEdit({
      name: c.name,
      sortOrder: String(c.sort_order ?? 0),
      printKitchen: c.print_kitchen ?? false,
      departmentId: String(c.department_id),
    });
  }

  async function saveCatEdit(id: string) {
    if (!supabase) return;
    const departmentId = parseDepartmentId(catEdit.departmentId);
    if (departmentId == null) {
      setError(t('menu.hdmDeptInvalid'));
      return;
    }
    setCatEditSaving(true);
    setError(null);
    const { error: err } = await supabase
      .from('categories')
      .update({
        name: catEdit.name.trim(),
        sort_order: Number(catEdit.sortOrder) || 0,
        print_kitchen: catEdit.printKitchen,
        department_id: departmentId,
      })
      .eq('id', id);
    setCatEditSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    await supabase.from('products').update({ department_id: departmentId }).eq('category_id', id);
    setCatEditId(null);
    void load();
  }

  async function addProduct() {
    if (!supabase) return;
    if (!clientId) {
      setError(t('menu.noClient'));
      return;
    }
    if (!form.name || !form.categoryId || !form.price) {
      setError(t('menu.productRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    const { error: err } = await supabase.from('products').insert({
      client_id: clientId,
      name: form.name,
      category_id: form.categoryId,
      price: Number(form.price),
      unit: form.unit,
      adg_code: form.adgCode,
      department_id: departmentIdForCategory(form.categoryId),
      product_code: form.productCode,
      recipe_id: form.recipeId || null,
      active: true,
    });
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setForm(BLANK);
    void load();
  }

  async function saveProdEdit(id: string) {
    if (!supabase) return;
    if (!prodEdit.name.trim() || !prodEdit.price) return;
    setProdEditSaving(true);
    setError(null);
    const { error: err } = await supabase
      .from('products')
      .update({
        name: prodEdit.name.trim(),
        price: Number(prodEdit.price),
        unit: prodEdit.unit.trim() || 'հատ',
        adg_code: prodEdit.adgCode.trim(),
        product_code: prodEdit.productCode.trim(),
        category_id: prodEdit.categoryId,
        department_id: departmentIdForCategory(prodEdit.categoryId),
      })
      .eq('id', id);
    setProdEditSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setProdEditId(null);
    void load();
  }

  async function assignRecipe(productId: string, recipeId: string | null) {
    if (!supabase) return;
    const { error: err } = await supabase
      .from('products')
      .update({ recipe_id: recipeId })
      .eq('id', productId);
    if (err) setError(err.message);
    else void load();
  }

  async function handleCsvImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !supabase || !clientId) return;
    e.target.value = '';
    setImporting(true);
    setImportResult(null);
    setError(null);
    try {
      const text = await readFileAsText(file);
      const rows = parseCsv(text);
      // Detect header: skip first row if it starts with a letter label like "name"
      const dataRows = /^[a-z_]/i.test(rows[0]?.[0] ?? '') ? rows.slice(1) : rows;
      // Resolve category by name (case-insensitive). Auto-create missing ones.
      const catByName = new Map(categories.map((c) => [c.name.trim().toLowerCase(), c.id]));
      const catDeptById = new Map(
        categories
          .filter((c) => c.department_id != null)
          .map((c) => [c.id, c.department_id] as const),
      );
      const newCatNames = new Set<string>();
      for (const r of dataRows) {
        const catName = r[1]?.trim();
        if (catName && !catByName.has(catName.toLowerCase())) newCatNames.add(catName);
      }
      // Create missing categories first
      if (newCatNames.size > 0) {
        const inserts = [...newCatNames].map((name, i) => ({
          client_id: clientId,
          name,
          sort_order: categories.length + i,
          department_id: 1,
        }));
        const { data: created, error: catErr } = await supabase
          .from('categories')
          .insert(inserts)
          .select('id, name');
        if (catErr) {
          setError(catErr.message);
          return;
        }
        for (const c of created ?? []) {
          catByName.set(c.name.trim().toLowerCase(), c.id);
          catDeptById.set(c.id, 1);
        }
      }
      // Expected columns: name, category, price, unit, adg_code, code
      const productInserts = dataRows
        .filter((r) => r[0]?.trim() && r[2]?.trim())
        .map((r) => {
          const categoryId = catByName.get(r[1]?.trim().toLowerCase() ?? '') ?? null;
          const deptId = categoryId ? catDeptById.get(categoryId) : undefined;
          return { row: r, categoryId, deptId };
        })
        .filter((x): x is typeof x & { categoryId: string; deptId: number } =>
          x.categoryId != null && x.deptId != null,
        )
        .map(({ row: r, categoryId, deptId }) => ({
          client_id: clientId,
          name: (r[0] ?? '').trim(),
          category_id: categoryId,
          price: Number(r[2]) || 0,
          unit: r[3]?.trim() || 'հատ',
          adg_code: r[4]?.trim() || '',
          department_id: deptId,
          product_code: r[5]?.trim() || '',
          active: true,
        }));
      if (productInserts.length === 0) {
        setImportResult(
          t('menu.importNoRows'),
        );
        return;
      }
      const { error: err } = await supabase.from('products').insert(productInserts);
      if (err) setError(err.message);
      else {
        setImportResult(t('menu.imported', { count: productInserts.length }));
        void load();
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setImporting(false);
    }
  }

  async function toggleActive(p: ProductRow) {
    if (!supabase) return;
    const { error: err } = await supabase
      .from('products')
      .update({ active: !p.active })
      .eq('id', p.id);
    if (err) setError(err.message);
    else void load();
  }

  async function remove(p: ProductRow) {
    if (!supabase) return;
    const { error: err } = await supabase.from('products').delete().eq('id', p.id);
    if (err) setError(err.message);
    else void load();
  }

  const catName = (id: string) => categories.find((c) => c.id === id)?.name ?? '—';

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => {
      const recipe = recipes.find((r) => r.id === p.recipe_id)?.name ?? '';
      const productCode = ((p as Record<string, unknown>)['product_code'] as string) ?? '';
      const haystack = [p.name, catName(p.category_id), p.unit ?? '', p.adg_code ?? '', productCode, recipe]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [products, categories, recipes, search]);

  function departmentIdForCategory(categoryId: string): number {
    const cat = categories.find((c) => c.id === categoryId);
    return cat?.department_id ?? 1;
  }

  function parseDepartmentId(raw: string): number | null {
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1) return null;
    return n;
  }

  return (
    <section className="ui-stack">
      <PageHeader title={t('menu.title')} />

      {error && <Callout tone="danger">{error}</Callout>}

      {/* ── Categories ─────────────────────────────────────────────── */}
      <h2>{t('menu.categories')}</h2>
      <div className="panel">
        <div className="add-form">
          <input
            placeholder={t('menu.namePlaceholder')}
            value={catForm.name}
            onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && void addCategory()}
          />
          <input
            placeholder={t('menu.sortPlaceholder')}
            type="number"
            min="0"
            style={{ maxWidth: 80 }}
            value={catForm.sortOrder}
            onChange={(e) => setCatForm({ ...catForm, sortOrder: e.target.value })}
          />
          <input
            placeholder={t('menu.hdmPlaceholder')}
            type="number"
            min="1"
            required
            title={t('menu.hdmTitle')}
            style={{ maxWidth: 90 }}
            value={catForm.departmentId}
            onChange={(e) => setCatForm({ ...catForm, departmentId: e.target.value })}
          />
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <input
              type="checkbox"
              checked={catForm.printKitchen}
              onChange={(e) => setCatForm({ ...catForm, printKitchen: e.target.checked })}
            />
            {t('menu.printKitchen')}
          </label>
          <button className="btn" onClick={addCategory} disabled={catSaving}>
            {catSaving ? '…' : t('menu.addCategory')}
          </button>
        </div>

        {loading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('menu.colName')}</th>
                <th>{t('menu.colOrder')}</th>
                <th>{t('menu.colHdm')}</th>
                <th style={{ textAlign: 'center' }}>{t('menu.colKitchen')}</th>
                <th>{t('menu.colRegister')}</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c) =>
                catEditId === c.id ? (
                  <tr key={c.id}>
                    <td>
                      <input
                        className="table-edit-input"
                        value={catEdit.name}
                        onChange={(e) => setCatEdit({ ...catEdit, name: e.target.value })}
                        autoFocus
                      />
                    </td>
                    <td>
                      <input
                        className="table-edit-input"
                        type="number"
                        min="0"
                        value={catEdit.sortOrder}
                        onChange={(e) => setCatEdit({ ...catEdit, sortOrder: e.target.value })}
                        style={{ width: 70 }}
                      />
                    </td>
                    <td>
                      <input
                        className="table-edit-input"
                        type="number"
                        min="1"
                        value={catEdit.departmentId}
                        onChange={(e) => setCatEdit({ ...catEdit, departmentId: e.target.value })}
                        style={{ width: 70 }}
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={catEdit.printKitchen}
                        onChange={(e) => setCatEdit({ ...catEdit, printKitchen: e.target.checked })}
                      />
                    </td>
                    <td />
                    <td>
                      <div className="row-actions">
                        <button
                          className="btn btn-sm"
                          onClick={() => void saveCatEdit(c.id)}
                          disabled={catEditSaving}
                        >
                          {catEditSaving ? '…' : t('common.save')}
                        </button>
                        <button className="btn-ghost btn-sm" onClick={() => setCatEditId(null)}>
                          {t('common.cancel')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={c.id} className={c.active === false ? 'inactive' : ''}>
                    <td>{c.name}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{c.sort_order}</td>
                    <td>
                      <span
                        className="color-chip"
                        style={{ background: departmentColor(c.department_id) }}
                        title={t('menu.deptColorTitle', { id: c.department_id })}
                      >
                        {c.department_id}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {c.print_kitchen ? (
                        <ChefHat className="ui-i" />
                      ) : (
                        <span style={{ color: 'var(--text-subtle)' }}>—</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="link"
                        onClick={() => setCategoryPending(c)}
                        title={c.active === false ? t('menu.showOnRegister') : t('menu.hideFromRegister')}
                      >
                        {c.active === false ? (
                          <span className="badge badge-muted">{t('common.hidden')}</span>
                        ) : (
                          <span className="badge badge-success">{t('common.active')}</span>
                        )}
                      </button>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button className="btn-icon" onClick={() => startCatEdit(c)} title={t('common.edit')}>
                          <Pencil className="ui-i" />
                        </button>
                        <button
                          className="btn-icon danger"
                          onClick={() => void removeCategory(c.id)}
                          title={t('common.delete')}
                        >
                          <Trash2 className="ui-i" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ),
              )}
              {categories.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      textAlign: 'center',
                      padding: '28px 20px',
                      color: 'var(--text-subtle)',
                    }}
                  >
                    {t('menu.noCategories')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Products ───────────────────────────────────────────────── */}
      <h2>{t('menu.products')}</h2>
      <div className="panel">
        <div className="add-form">
          <input
            placeholder={t('menu.namePlaceholder')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <select
            value={form.categoryId}
            onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
          >
            <option value="">{t('menu.categoryPlaceholder')}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <input
            placeholder={t('menu.pricePlaceholder')}
            type="number"
            min="0"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            style={{ maxWidth: 100 }}
          />
          <input
            placeholder={t('menu.unitPlaceholder')}
            value={form.unit}
            onChange={(e) => setForm({ ...form, unit: e.target.value })}
            style={{ maxWidth: 80 }}
          />
          <input
            placeholder={t('menu.adgPlaceholder')}
            value={form.adgCode}
            onChange={(e) => setForm({ ...form, adgCode: e.target.value })}
            style={{ maxWidth: 100 }}
          />
          <input
            placeholder={t('menu.codePlaceholder')}
            value={form.productCode}
            onChange={(e) => setForm({ ...form, productCode: e.target.value })}
            style={{ maxWidth: 90 }}
          />
          <select
            value={form.recipeId}
            onChange={(e) => setForm({ ...form, recipeId: e.target.value })}
          >
            <option value="">{t('menu.noRecipe')}</option>
            {recipes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          <button className="btn" onClick={addProduct} disabled={saving}>
            {saving ? '…' : t('menu.addProduct')}
          </button>
          <input
            type="search"
            className="panel-search"
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {!loading && (
          <table className="table">
            <thead>
              <tr>
                <th>{t('menu.colProduct')}</th>
                <th>{t('menu.colCategory')}</th>
                <th>{t('menu.colPrice')}</th>
                <th>{t('menu.colUnit')}</th>
                <th>{t('menu.colAdg')}</th>
                <th>{t('menu.colRecipe')}</th>
                <th>{t('menu.colCost')}</th>
                <th>{t('menu.colStatus')}</th>
                <th style={{ width: 70 }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((p) =>
                prodEditId === p.id ? (
                  <tr key={p.id}>
                    <td>
                      <input
                        className="table-edit-input"
                        value={prodEdit.name}
                        onChange={(e) => setProdEdit({ ...prodEdit, name: e.target.value })}
                        autoFocus
                        style={{ minWidth: 140 }}
                      />
                    </td>
                    <td>
                      <select
                        value={prodEdit.categoryId}
                        onChange={(e) => setProdEdit({ ...prodEdit, categoryId: e.target.value })}
                        style={{ fontSize: 12, padding: '3px 6px' }}
                      >
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="table-edit-input"
                        type="number"
                        min="0"
                        value={prodEdit.price}
                        onChange={(e) => setProdEdit({ ...prodEdit, price: e.target.value })}
                        style={{ width: 90 }}
                      />
                    </td>
                    <td>
                      <input
                        className="table-edit-input"
                        value={prodEdit.unit}
                        onChange={(e) => setProdEdit({ ...prodEdit, unit: e.target.value })}
                        style={{ width: 70 }}
                      />
                    </td>
                    <td>
                      <input
                        className="table-edit-input"
                        value={prodEdit.adgCode}
                        onChange={(e) => setProdEdit({ ...prodEdit, adgCode: e.target.value })}
                        style={{ width: 80 }}
                      />
                    </td>
                    <td colSpan={3} style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                      save to edit recipe
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="btn btn-sm"
                          onClick={() => void saveProdEdit(p.id)}
                          disabled={prodEditSaving}
                        >
                          {prodEditSaving ? '…' : t('common.save')}
                        </button>
                        <button className="btn-ghost btn-sm" onClick={() => setProdEditId(null)}>
                          {t('common.cancel')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={p.id} className={p.active ? '' : 'inactive'}>
                    <td>{p.name}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{catName(p.category_id)}</td>
                    <td>{AMD(p.price)}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{p.unit}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{p.adg_code || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <select
                          value={p.recipe_id ?? ''}
                          onChange={(e) => assignRecipe(p.id, e.target.value || null)}
                          style={{ fontSize: 12, padding: '3px 6px', flex: 1, minWidth: 100 }}
                        >
                          <option value="">—</option>
                          {recipes.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                        {p.recipe_id && onNavigateToRecipe && (
                          <button
                            type="button"
                            className="link"
                            style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                            title={t('menu.openRecipe')}
                            onClick={() => onNavigateToRecipe(p.recipe_id!)}
                          >
                            {t('menu.openRecipe')} →
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      {(() => {
                        const cost = recipeCostByProduct.get(p.id);
                        if (!p.recipe_id)
                          return <span style={{ color: 'var(--text-subtle)' }}>—</span>;
                        if (cost === undefined || (cost.min === 0 && cost.max === 0))
                          return <span style={{ color: 'var(--text-subtle)' }}>—</span>;
                        const pctColor = (pct: number) =>
                          pct > 40
                            ? 'var(--danger, #e53e3e)'
                            : pct > 25
                              ? 'var(--warning, #dd6b20)'
                              : 'var(--success, #38a169)';
                        if (cost.min === cost.max) {
                          const pct = Math.round((cost.min / p.price) * 100);
                          return (
                            <span>
                              {AMD(Math.round(cost.min))}
                              <span
                                style={{ marginLeft: 6, fontSize: 11, color: pctColor(pct) }}
                              >
                                {pct}%
                              </span>
                            </span>
                          );
                        }
                        const minPct = Math.round((cost.min / p.price) * 100);
                        const maxPct = Math.round((cost.max / p.price) * 100);
                        return (
                          <span>
                            {`${Math.round(cost.min).toLocaleString('en-US')}–${Math.round(cost.max).toLocaleString('en-US')} ֏`}
                            <span
                              style={{ marginLeft: 6, fontSize: 11, color: pctColor(maxPct) }}
                            >
                              {minPct}–{maxPct}%
                            </span>
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      <button className="link" onClick={() => toggleActive(p)}>
                        {p.active ? (
                          <span className="badge badge-success">{t('common.active')}</span>
                        ) : (
                          <span className="badge badge-muted">{t('common.hidden')}</span>
                        )}
                      </button>
                    </td>
                    <td>
                      <button className="btn-icon danger" onClick={() => remove(p)} title={t('common.delete')}>
                        <Trash2 className="ui-i" />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => {
                          setProdEditId(p.id);
                          setProdEdit({
                            name: p.name,
                            price: String(p.price),
                            unit: p.unit ?? '',
                            adgCode: p.adg_code ?? '',
                            productCode:
                              ((p as Record<string, unknown>)['product_code'] as string) ?? '',
                            categoryId: p.category_id,
                          });
                        }}
                        title={t('common.edit')}
                      >
                        <Pencil className="ui-i" />
                      </button>
                    </td>
                  </tr>
                ),
              )}
              {filteredProducts.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    style={{
                      textAlign: 'center',
                      padding: '28px 20px',
                      color: 'var(--text-subtle)',
                    }}
                  >
                    {products.length === 0
                      ? t('menu.noProducts')
                      : t('menu.noSearchMatch', { query: search.trim() })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        <div className="csv-import">
          <input
            ref={csvRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleCsvImport}
          />
          <button
            className="btn-ghost btn-sm"
            onClick={() => csvRef.current?.click()}
            disabled={importing}
          >
            {importing ? t('common.importing') : `↑ ${t('menu.importCsv')}`}
          </button>
          <span className="csv-hint">
            {t('menu.importHint')}
          </span>
          {importResult && <span className="csv-result">{importResult}</span>}
        </div>
      </div>

      {/* ── Price list by category ─────────────────────────────────── */}
      <div className="price-list-head">
        <h2>{t('menu.priceList')}</h2>
        <button className="btn-secondary no-print" onClick={() => window.print()}>
          <Printer className="ui-i" /> {t('menu.print')}
        </button>
      </div>
      <div className="panel price-list-panel" style={{ padding: 20 }}>
        <div className="cat-chips no-print">
          <button
            className={`cat-chip${priceListCat === '' ? ' active' : ''}`}
            onClick={() => setPriceListCat('')}
          >
            {t('menu.allCategories')}
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              className={`cat-chip${priceListCat === c.id ? ' active' : ''}`}
              onClick={() => setPriceListCat(c.id)}
            >
              {c.name}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : (
          (() => {
            const shownCats = categories.filter(
              (c) => priceListCat === '' || c.id === priceListCat,
            );
            const rendered = shownCats
              .map((c) => ({
                cat: c,
                items: products.filter((p) => p.category_id === c.id),
              }))
              .filter((g) => g.items.length > 0);
            if (rendered.length === 0) {
              return <div className="empty-state">{t('menu.emptyCategory')}</div>;
            }
            return (
              <div className="price-list">
                {rendered.map(({ cat, items }) => (
                  <div key={cat.id} className="price-list-group">
                    <h3 className="price-list-cat">{cat.name}</h3>
                    {items.map((p) => {
                      const variations = variationsByProduct.get(p.id) ?? [];
                      return (
                        <div key={p.id} className={`price-list-item${p.active ? '' : ' inactive'}`}>
                          <div className="price-list-row">
                            <span className="price-list-name">
                              {p.name}
                              {!p.active && (
                                <span className="badge badge-muted" style={{ marginLeft: 8 }}>
                                  Hidden
                                </span>
                              )}
                            </span>
                            <span className="price-list-dots" />
                            <span className="price-list-price">{AMD(p.price)}</span>
                          </div>
                          {variations.map((v, i) => (
                            <div key={i} className="price-list-row price-list-variation">
                              <span className="price-list-name">{v.label}</span>
                              <span className="price-list-dots" />
                              <span className="price-list-price">
                                {v.priceDelta !== 0 && (
                                  <span className="price-list-delta">
                                    {v.priceDelta > 0 ? '+' : '−'}
                                    {AMD(Math.abs(v.priceDelta))}{' '}
                                  </span>
                                )}
                                {AMD(p.price + v.priceDelta)}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })()
        )}
      </div>

      <Modal
        open={categoryPending != null}
        onClose={() => {
          if (!categoryToggleBusy) setCategoryPending(null);
        }}
        title={
          categoryPending && categoryPending.active === false
            ? t('menu.confirmShowTitle', { name: categoryPending.name })
            : t('menu.confirmHideTitle', { name: categoryPending?.name ?? '' })
        }
        footer={
          <>
            <Button
              variant="secondary"
              disabled={categoryToggleBusy}
              onClick={() => setCategoryPending(null)}
            >
              {t('common.cancel')}
            </Button>
            <Button variant="primary" disabled={categoryToggleBusy} onClick={() => void confirmCategoryToggle()}>
              {categoryToggleBusy
                ? '…'
                : categoryPending?.active === false
                  ? t('menu.showOnRegister')
                  : t('menu.hideFromRegister')}
            </Button>
          </>
        }
      >
        <p className="muted">
          {categoryPending?.active === false ? t('menu.confirmShowBody') : t('menu.confirmHideBody')}
        </p>
      </Modal>
    </section>
  );
}
